/**
 * LabDeckForms.tsx — the forms of Admin > Lab Decks, with legacy's fields
 * (admin-labdeck-list.php's dialogs):
 *
 *   - Deck: Name *.
 *   - Slide: Deck *, Title, Type (HTML Content, or Lab Results: Left-side and
 *     Right-side content around the slide's tests), and, once saved, its Tests.
 *   - Test: Title *, Diagnosis Code (OBX) and / or Biomarker (one at least),
 *     Description, Icon, three ranges (Title, Color, Low – High), Increment,
 *     Gender, Optimal Text.
 *
 * The viewer tells an HTML slide by its content, so a Lab Results slide keeps
 * none. Legacy could not add tests to a slide before it was saved; neither can
 * this, but the slide stays open once created so tests can follow at once.
 *
 * On a Locked page (useAdminPageLevel) every form is AdminFormModal's viewer.
 * The slide's tests have no Add test or Delete, but each keeps a View icon (a
 * test row does not open on a click); a test shows its icon and range colours
 * with no upload, Remove or colour picker. Nothing says why (R18).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, Plus, X } from 'lucide-react';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { RichTextEditor } from '@/components/admin/RichTextEditor';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { getErrorMessage } from '@/services/apiClient';
import {
  DECK_TITLE_MAX,
  GENDER_OPTIONS,
  ICON_ACCEPT,
  ICON_MAX_BYTES,
  blankTest,
  createLabDeck,
  createLabSlide,
  createSlideTest,
  deleteSlideTest,
  fetchDeckIconUrl,
  isAcceptedIcon,
  listSlideTests,
  renameLabDeck,
  updateLabSlide,
  updateSlideTest,
  uploadDeckIcon,
  type Biomarker,
  type DeckTest,
  type LabDeck,
  type LabSlide,
  type SlideKind,
  type TestRange,
} from '@/services/labDecksAdmin.service';
import { toast } from '@/store/toastStore';

// ─── Deck ─────────────────────────────────────────────────────────────────

export function LabDeckModal({ deck, onClose, onSaved }: { deck: LabDeck | null; onClose: () => void; onSaved: () => void }) {
  const isNew = deck === null;
  const t = useTrackedForm(() => ({ title: deck?.title ?? '' }), { tracking: !isNew });
  const [saving, setSaving] = useState(false);
  const missing = t.form.title.trim() === '';

  async function save() {
    if (missing) return;
    setSaving(true);
    try {
      if (deck) await renameLabDeck(deck.id, t.form.title);
      else await createLabDeck(t.form.title);
      toast.success(isNew ? 'Deck created' : 'Deck updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The deck could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Deck' : 'Edit Deck'}
      isNew={isNew}
      dirty={isNew ? !missing : t.dirty}
      saving={saving}
      errors={missing ? ['Name is required'] : []}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="md"
    >
      <DialogField
        label="Name"
        htmlFor="lab-deck-title"
        required
        state={missing ? 'error' : 'none'}
        labelHint={missing ? '— required' : undefined}
        badge={t.badge('title', 'Name')}
      >
        <Input
          id="lab-deck-title"
          value={t.form.title}
          maxLength={DECK_TITLE_MAX}
          placeholder="e.g. Hormone panel"
          onChange={(e) => t.patch({ title: e.target.value })}
          className={fieldClasses.input(missing ? 'error' : 'none')}
        />
      </DialogField>
    </AdminFormModal>
  );
}

// ─── Slide ────────────────────────────────────────────────────────────────

const KIND_OPTIONS: { value: SlideKind; label: string }[] = [
  { value: 'html', label: 'HTML Content' },
  { value: 'lab', label: 'Lab Results' },
];

export function LabSlideModal({
  slide,
  defaultDeckId,
  decks,
  biomarkers,
  onTestsChanged,
  onClose,
  onSaved,
}: {
  slide: LabSlide | null;
  defaultDeckId: number;
  /** Live decks (the slide's own too). */
  decks: LabDeck[];
  biomarkers: Biomarker[];
  /** A test was added, changed or removed (the slide list shows test counts). */
  onTestsChanged: () => void;
  onClose: () => void;
  /** After a save; `createdId` when the slide was just created (it stays open). */
  onSaved: (createdId?: number) => void;
}) {
  const isNew = slide === null;
  const t = useTrackedForm(
    () => ({
      deckId: slide?.deckId ?? defaultDeckId,
      title: slide?.title ?? '',
      kind: (slide?.kind ?? 'lab') as SlideKind,
      customContent: slide?.customContent ?? '',
      leftContent: slide?.leftContent ?? '',
      rightContent: slide?.rightContent ?? '',
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const blankHtml = t.form.kind === 'html' && t.form.customContent.replace(/<(?!img)[^>]*>/gi, '').replace(/&nbsp;/g, ' ').trim() === '';
  const errors = [...(!t.form.deckId ? ['Deck is required'] : []), ...(blankHtml ? ['An HTML slide needs its content'] : [])];

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    try {
      if (slide) {
        await updateLabSlide(slide.id, t.form);
        toast.success('Slide updated');
        onSaved();
      } else {
        const id = await createLabSlide(t.form);
        toast.success('Slide created');
        onSaved(id);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'The slide could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Slide' : 'Edit Slide'}
      isNew={isNew}
      dirty={isNew ? errors.length === 0 : t.dirty}
      saving={saving}
      errors={errors}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="xl"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DialogField
          label="Deck"
          htmlFor="lab-slide-deck"
          required
          badge={t.badge('deckId', 'Deck')}
          description={!isNew && t.form.deckId !== slide?.deckId ? 'It moves to the end of this deck.' : undefined}
        >
          <Select<number>
            id="lab-slide-deck"
            options={decks.map((d) => ({ value: d.id, label: d.title.trim() || `Deck #${d.id}` }))}
            value={t.form.deckId || null}
            onChange={(v) => t.patch({ deckId: v ?? 0 })}
          />
        </DialogField>
        <DialogField label="Type" htmlFor="lab-slide-kind" badge={t.badge('kind', 'Type')}>
          <Select<SlideKind> id="lab-slide-kind" options={KIND_OPTIONS} value={t.form.kind} onChange={(v) => t.patch({ kind: v ?? 'lab' })} />
        </DialogField>
      </div>
      <DialogField label="Title" htmlFor="lab-slide-title" badge={t.badge('title', 'Title')}>
        <Input id="lab-slide-title" value={t.form.title} maxLength={255} onChange={(e) => t.patch({ title: e.target.value })} />
      </DialogField>

      {t.form.kind === 'html' ? (
        <DialogField label="HTML Content" htmlFor="lab-slide-html" required state={blankHtml ? 'error' : 'none'} badge={t.badge('customContent', 'HTML Content')}>
          <RichTextEditor id="lab-slide-html" value={t.form.customContent} onChange={(customContent) => t.patch({ customContent })} invalid={blankHtml} />
        </DialogField>
      ) : (
        <>
          <DialogField label="Left-side Content" htmlFor="lab-slide-left" badge={t.badge('leftContent', 'Left-side Content')}>
            <RichTextEditor id="lab-slide-left" value={t.form.leftContent} onChange={(leftContent) => t.patch({ leftContent })} />
          </DialogField>
          <DialogField label="Right-side Content" htmlFor="lab-slide-right" badge={t.badge('rightContent', 'Right-side Content')}>
            <RichTextEditor id="lab-slide-right" value={t.form.rightContent} onChange={(rightContent) => t.patch({ rightContent })} />
          </DialogField>
          {slide ? (
            <SlideTests slideId={slide.id} biomarkers={biomarkers} onChanged={onTestsChanged} />
          ) : (
            <p className="text-sm text-slate-500">Add the slide first; its tests can be added right after.</p>
          )}
        </>
      )}
    </AdminFormModal>
  );
}

/** A lab slide's tests (legacy's Tests list), each saved on its own. */
function SlideTests({ slideId, biomarkers, onChanged }: { slideId: number; biomarkers: Biomarker[]; onChanged: () => void }) {
  const locked = useAdminPageLevel() === 'locked';
  const [tests, setTests] = useState<DeckTest[] | null>(null);
  const [editing, setEditing] = useState<DeckTest | null>(null);
  const [deleting, setDeleting] = useState<DeckTest | null>(null);

  const load = useMemo(
    () => () =>
      listSlideTests(slideId)
        .then(setTests)
        .catch((err) => {
          setTests([]);
          toast.error(getErrorMessage(err, 'The tests could not be loaded.'));
        }),
    [slideId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const biomarkerName = (id: number) => {
    const b = biomarkers.find((x) => x.id === id);
    return b ? (b.unit ? `${b.name} (${b.unit})` : b.name) : id ? `Biomarker #${id}` : '';
  };

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteSlideTest(deleting.id);
      toast.success('Test deleted');
      void load();
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The test could not be deleted.'));
    }
  }

  return (
    <DialogField label="Tests" description="Each test is matched to the patient's result by its biomarker or Diagnosis Code, and drawn as a gauge over its ranges.">
      <div className="space-y-2" data-testid="slide-tests">
        {tests === null ? (
          <p className="text-sm text-slate-500">Loading tests…</p>
        ) : tests.length === 0 ? (
          <p className="text-sm text-slate-500">{locked ? 'No tests.' : 'No tests yet.'}</p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded border border-slate-200">
            {tests.map((test) => (
              <li key={test.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid="slide-test">
                <span className="min-w-0 flex-1 truncate font-medium text-slate-900">{test.title || '(untitled)'}</span>
                <span className="hidden truncate text-slate-500 sm:block">{[test.labCode, biomarkerName(test.biomarkerId)].filter(Boolean).join(' · ')}</span>
                <AdminRowActions>
                  <AdminRowAction kind="edit" label={`Edit test ${test.title}`} onClick={() => setEditing(test)} viewWhenLocked />
                  <AdminRowAction kind="delete" label={`Delete test ${test.title}`} onClick={() => setDeleting(test)} />
                </AdminRowActions>
              </li>
            ))}
          </ul>
        )}
        {!locked && (
          <Button variant="secondary" size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing(blankTest())}>
            Add test
          </Button>
        )}
      </div>

      {editing && (
        <DeckTestModal
          key={editing.id || 'new'}
          slideId={slideId}
          test={editing}
          biomarkers={biomarkers}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
            onChanged();
          }}
        />
      )}
      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete test "${deleting.title}"?` : ''}
        message="It is removed from the slide for good."
        confirmLabel="Delete test"
        variant="danger"
      />
    </DialogField>
  );
}

// ─── Test ─────────────────────────────────────────────────────────────────

const NUMBER = /^\d*(\.\d*)?$/;

function DeckTestModal({
  slideId,
  test,
  biomarkers,
  onClose,
  onSaved,
}: {
  slideId: number;
  test: DeckTest;
  biomarkers: Biomarker[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = test.id === 0;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm(() => ({ ...test, ranges: test.ranges.map((r) => ({ ...r })) as DeckTest['ranges'] }), { tracking: !isNew });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const iconUrl = useDeckIcon(t.form.iconImage);

  const f = t.form;
  const noTitle = f.title.trim() === '';
  const noCode = f.labCode.trim() === '' && !f.biomarkerId;
  const badIncrement = !(Number(f.increment) > 0);
  const badRanges = f.ranges
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => !NUMBER.test(r.low.trim()) || !NUMBER.test(r.high.trim()) || (r.low.trim() !== '' && r.high.trim() !== '' && Number(r.low) > Number(r.high)));

  const errors = [
    ...(noTitle ? ['Title is required'] : []),
    ...(noCode ? ['Enter at least a Diagnosis Code (OBX) or select a Biomarker'] : []),
    ...(badIncrement ? ['Increment must be more than 0'] : []),
    ...badRanges.map(({ i }) => `Range ${i + 1}: values must be numbers 0 or above, low no higher than high`),
  ];

  const biomarkerOptions = biomarkers.map((b) => ({ value: b.id, label: b.unit ? `${b.name} (${b.unit})` : b.name }));

  function setRange(i: number, patch: Partial<TestRange>) {
    t.patch({ ranges: f.ranges.map((r, j) => (j === i ? { ...r, ...patch } : r)) as DeckTest['ranges'] });
  }

  async function pickIcon(file: File | undefined) {
    if (!file) return;
    if (!isAcceptedIcon(file)) {
      toast.error('Upload a jpg or png image.');
      return;
    }
    if (file.size > ICON_MAX_BYTES) {
      toast.error('The image must be 4 MB or smaller.');
      return;
    }
    setUploading(true);
    try {
      t.patch({ iconImage: await uploadDeckIcon(file) });
    } catch (err) {
      toast.error(getErrorMessage(err, 'The image could not be uploaded.'));
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    try {
      if (isNew) await createSlideTest(slideId, f);
      else await updateSlideTest(test.id, f);
      toast.success(isNew ? 'Test added' : 'Test updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The test could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Test' : `Edit Test: ${test.title}`}
      isNew={isNew}
      dirty={isNew ? errors.length === 0 : t.dirty}
      saving={saving || uploading}
      errors={errors}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="lg"
    >
      <DialogField label="Title" htmlFor="deck-test-title" required state={noTitle ? 'error' : 'none'} badge={t.badge('title', 'Title')}>
        <Input id="deck-test-title" value={f.title} maxLength={255} onChange={(e) => t.patch({ title: e.target.value })} className={fieldClasses.input(noTitle ? 'error' : 'none')} />
      </DialogField>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DialogField
          label="Diagnosis Code (OBX)"
          htmlFor="deck-test-code"
          state={noCode ? 'error' : 'none'}
          badge={t.badge('labCode', 'Diagnosis Code')}
          description={locked ? undefined : 'Enter at least one: Diagnosis Code or Biomarker. You may set both.'}
        >
          <Input id="deck-test-code" value={f.labCode} maxLength={50} onChange={(e) => t.patch({ labCode: e.target.value })} />
        </DialogField>
        <DialogField label="Biomarker" htmlFor="deck-test-biomarker" state={noCode ? 'error' : 'none'} badge={t.badge('biomarkerId', 'Biomarker')}>
          <SearchableCombobox<number>
            id="deck-test-biomarker"
            placeholder="None"
            options={biomarkerOptions}
            value={f.biomarkerId || null}
            onChange={(v) => t.patch({ biomarkerId: v ?? 0 })}
            clearable
            emptyMessage="No biomarkers found"
          />
        </DialogField>
      </div>
      <DialogField label="Description" htmlFor="deck-test-description" badge={t.badge('description', 'Description')}>
        <Textarea autoResize id="deck-test-description" rows={3} value={f.description} onChange={(e) => t.patch({ description: e.target.value })} />
      </DialogField>
      <DialogField label="Icon" badge={t.badge('iconImage', 'Icon')}>
        <div className="flex items-center gap-3">
          {iconUrl && <img src={iconUrl} alt="Test icon" className="h-12 w-12 rounded border border-slate-200 object-contain" />}
          {locked ? (
            !f.iconImage && <span className="text-sm text-slate-500">No icon</span>
          ) : (
            <>
              <input ref={fileRef} type="file" accept={ICON_ACCEPT} className="hidden" aria-label="Icon image file" onChange={(e) => void pickIcon(e.target.files?.[0])} />
              <Button variant="secondary" size="sm" leftIcon={<ImagePlus className="h-4 w-4" />} loading={uploading} onClick={() => fileRef.current?.click()}>
                {f.iconImage ? 'Replace icon' : 'Add icon'}
              </Button>
              {f.iconImage && (
                <Button variant="ghost" size="sm" leftIcon={<X className="h-4 w-4" />} onClick={() => t.patch({ iconImage: '' })}>
                  Remove
                </Button>
              )}
            </>
          )}
        </div>
      </DialogField>

      <DialogField label="Ranges" badge={t.badge('ranges', 'Ranges')} description="The gauge runs from Range 1's low value to Range 3's high value.">
        <div className="space-y-2" data-testid="deck-test-ranges">
          {f.ranges.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_5rem_5rem] items-center gap-2">
              <Input aria-label={`Range ${i + 1} title`} placeholder={`Range ${i + 1} title`} value={r.title} maxLength={100} onChange={(e) => setRange(i, { title: e.target.value })} />
              {locked ? (
                // A plain swatch: the native colour picker has no read-only mode.
                <span aria-hidden className="block h-9 w-10 rounded border border-slate-200 p-0.5">
                  <span className="block h-full w-full rounded-sm" style={{ backgroundColor: /^#[0-9a-f]{6}$/i.test(r.color) ? r.color : '#cccccc' }} />
                </span>
              ) : (
                <input
                  type="color"
                  aria-label={`Range ${i + 1} color`}
                  value={/^#[0-9a-f]{6}$/i.test(r.color) ? r.color : '#cccccc'}
                  onChange={(e) => setRange(i, { color: e.target.value })}
                  className="h-9 w-10 cursor-pointer rounded border border-slate-200"
                />
              )}
              <Input aria-label={`Range ${i + 1} low`} placeholder="Low" inputMode="decimal" value={r.low} onChange={(e) => setRange(i, { low: e.target.value.trim() })} />
              <Input aria-label={`Range ${i + 1} high`} placeholder="High" inputMode="decimal" value={r.high} onChange={(e) => setRange(i, { high: e.target.value.trim() })} />
            </div>
          ))}
        </div>
      </DialogField>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <DialogField label="Increment" htmlFor="deck-test-increment" required state={badIncrement ? 'error' : 'none'} badge={t.badge('increment', 'Increment')}>
          <Input id="deck-test-increment" inputMode="decimal" value={f.increment} onChange={(e) => t.patch({ increment: e.target.value.trim() })} />
        </DialogField>
        <DialogField label="Gender" htmlFor="deck-test-gender" badge={t.badge('gender', 'Gender')}>
          <Select<'' | 'F' | 'M'> id="deck-test-gender" options={GENDER_OPTIONS} value={f.gender} onChange={(v) => t.patch({ gender: v ?? '' })} />
        </DialogField>
        <DialogField label="Optimal Text" htmlFor="deck-test-optimal" badge={t.badge('optimalText', 'Optimal Text')}>
          <Input id="deck-test-optimal" value={f.optimalText} maxLength={255} onChange={(e) => t.patch({ optimalText: e.target.value })} />
        </DialogField>
      </div>
    </AdminFormModal>
  );
}

/** A test icon as an object URL (it lives in the account's folder, so it comes through the API). */
function useDeckIcon(fileName: string): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    setUrl(undefined);
    if (!fileName) return;
    let made: string | null = null;
    let cancelled = false;
    fetchDeckIconUrl(fileName)
      .then((u) => {
        made = u;
        if (!cancelled) setUrl(u ?? undefined);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [fileName]);
  return url;
}
